import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { Location } from "@angular/common";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Plant } from "@models/plant.model";
import { NavigationService } from "@app/core/services/navigation.service";
import { JWTUser } from "@models/jwt-data.model";
import { PermissionService } from "@app/authentication/services/permission.service";
import { Notification } from "@models/notification.model";
import { Subscription } from "rxjs/Rx";
import { NotificationService } from "@services/notification.service";
import { Log } from "ng2-logger";
import { AuthenticationService } from "@app/authentication/services/authentication.service";
import { SidebarVisibilityService } from "@app/core/services/sidebar-visibility.service";
import { TranslateService } from "@ngx-translate/core";
import { Menu } from "primeng/menu";
import * as _ from "lodash";

interface AssignedRoleOption {
    id: number;
    name: string;
    label: string;
}

const URLS = {
    TOOL_REQUESTS_LINES: '/tool-requests-lines',
    TOOL_REQUESTS_FACILITIES: '/tool-requests-facilities',
    TOOL_RETURNS_LINES: '/tool-returns-lines',
    TOOL_RETURNS_FACILITIES: '/tool-returns-facilities',

    LIFT_TOOL_REQUESTS_LINE: '/incoming-tool-requests/lines',
    LIFT_TOOL_REQUESTS_FACILITIES: '/incoming-tool-requests/facilities',
    LIFT_TOOL_RETURNS_LINES: '/incoming-tools-returned/lines',
    LIFT_TOOL_RETURNS_FACILITIES: '/incoming-tools-returned/facilities',

    LINE_SETTER_PENDING_TOOL_REQUESTS: '/pending-tool-requests',

    SERVICE_INCOMING_TICKETS: '/service-tickets',
    CREATE_MANAGE_TICKETS: '/manage-tickets-create',
    UPDATE_MANAGE_TICKETS: '/manage-tickets-update'
};

/**
 * TODO This component totally needs refactoring with regards tot he state concerning the plants. It's medieval!
 */
@Component({
    selector: "app-toolbar",
    templateUrl: "app-toolbar.component.html",
    styleUrls: ["app-toolbar.component.scss"],
})
export class AppToolbarComponent implements OnChanges, OnInit {
    _logger = Log.create(AppToolbarComponent.name);

    @Input() currentUser: JWTUser;
    @Input() currentLanguage: string;
    @Input() languages = [];
    @Input() hasSidebar: boolean;
    @Input() hideShadow = false;
    @Input() hasPlantDropdown: boolean;
    @Input() allPlants: Plant[] = [];
    @Input() selectedPlants: number[] = [];
    @Input() isMobile = false;
    @Input() availableRoles: AssignedRoleOption[] = [];
    @Input() selectedRoleId: number;
    @Input() isRoleSwitchInProgress = false;

    currentLocation: string;
    pathSplitFull: string[];
    notMainPath = false;

    visibleSidebar = false;  // Sidebar visibility state
    desktopAdaptedPlants = [];

    missedNotifications
    visitedUrls = new Map<string, boolean>();
    selectedPlantsIds: string[];
    selectedPlantsChangedSubscription: Subscription;
    receivedPlantsSubscription: Subscription;

    newLiftOperatorToIncomingToolRequests: Subscription;
    newLiftOperatorToIncomingFacilityToolRequests: Subscription;
    newLiftOperatorToIncomingToolReturns: Subscription;
    newLiftOperatorToIncomingFacilityToolReturns: Subscription;
    processLineToolRequests = 0;
    processLineToolReturns = 0;
    processFacilityToolRequests = 0;
    processFacilityToolReturns = 0;
    kebabMenuItems: any[] = [];

    private sidebarItemDefinitions = [
        { id: 'strain', translationKey: 'nav.sections.strain.title' },
        { id: 'tool', translationKey: 'nav.sections.tools.title' }
    ];
    availableSidebarItems = [];

    selectedSidebarItems: string[] = [];
    selectedSidebarItemObjects: any[] = [];
    hasToolSection = false;
    hasStrainSection = false;
    showSectionSelector = false;

    @Output("logoutFired") logoutFired: EventEmitter<any> = new EventEmitter();
    @Output("languageChange") languageChange: EventEmitter<any> =
        new EventEmitter();
    @Output("roleChange") roleChange: EventEmitter<any> = new EventEmitter();
    @Output("selectedPlantsChange") selectedPlantsChange: EventEmitter<any> =
        new EventEmitter();

    @HostBinding('class.z-depth-1') get isElevated() {
        return !this.hideShadow;
    }

    @HostBinding('class.navigation--milkrun') get isMilkrun() {
        return this.currentLocation && this.currentLocation === '/transportation-requests';
    }


    constructor(private location: Location,
                private router: Router,
                private translateService: TranslateService,
                private authService: AuthenticationService,
                private navigationService: NavigationService,
                private permissionService: PermissionService,
                private notificationService: NotificationService,
                private sidebarItemsService: SidebarVisibilityService
                ) {

        this.currentLocation = this.router.url;
        this.pathSplitFull = this.router.url.split("/");
        this.notMainPath = false;

        this.translateSidebarItems();

        this.sidebarItemsService.sidebarItems.subscribe(items => {
            this.selectedSidebarItems = items;
            this.syncSidebarObjects();
            });

        // Subscribing to URL Change
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.visitedUrls.set(this.router.url, false);
                switch (this.router.url) {
                    case '/incoming-tool-requests/lines': {
                        this.processLineToolRequests = 0;
                        break;
                    }
                    case '/incoming-tools-returned/lines': {
                        this.processLineToolReturns = 0;
                        break;
                    }
                    case '/incoming-tool-requests/facilities': {
                        this.processFacilityToolRequests = 0;
                        break;
                    }
                    case '/incoming-tools-returned/facilities': {
                        this.processFacilityToolReturns = 0;
                        break;
                    }
                    default: {

                    }
                }
            }
            if (event instanceof NavigationEnd) {
                this.currentLocation = event.url;
                this.pathSplitFull = event.urlAfterRedirects
                    .split("/")
                    .filter((x) => x !== "");

                this.notMainPath = this.pathSplitFull[1] !== undefined;
            }
        });

        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(() => {
            this.missedNotifications.toolRequests.approveLineRequests = 0;
            this.missedNotifications.toolReturns.approveLineRequests = 0;
            this.missedNotifications.toolRequests.processLineRequests = 0;
            this.missedNotifications.toolReturns.processLineRequests = 0;
            this.resetIncomingNotifications();
        });

        this.kebabMenuItems = this.buildKebabMenuItems()
    }

    ngOnInit(): void {
        this.evaluateSectionSelector();

        if (this.showSectionSelector) {
            this.syncSidebarObjects();
        }

        this.translateService.onLangChange.subscribe(() => this.translateSidebarItems());

        // Lift operator queues
        this.subscribeLiftOperatorToIncomingToolRequests();
        this.subscribeLiftOperatorToIncomingFacilityToolRequests();
        this.subscribeLiftOperatorToIncomingToolReturns();
        this.subscribeLiftOperatorToIncomingFacilityToolReturns();
        // Service Operator
        this.receivedPlantsSubscription = this.notificationService.selectedPlantsReceivedFromAPIAsObservable.subscribe(() => {
                this.selectedPlantsIds = this.getSelectedPlants();
                this.getNotifications(this.selectedPlantsIds);
            }
        )
        this.kebabMenuItems = this.buildKebabMenuItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedPlants || changes.allPlants) {
            const selectedPlants = Array.isArray(this.selectedPlants) ? this.selectedPlants : [];
            const allPlants = Array.isArray(this.allPlants) ? this.allPlants : [];

            this.desktopAdaptedPlants = selectedPlants
                .map((id) => allPlants.find((plant) => plant.id === id))
                .filter((plant) => plant !== undefined) as { id: number; name: string }[];
            this.kebabMenuItems = this.buildKebabMenuItems();
        }

        if (changes.currentLanguage) {
            this.translateSidebarItems();
        }

        if (changes.currentUser && changes.currentUser.currentValue) {
            this.evaluateSectionSelector();
        }
    }

    // Toggles the sidebar visibility
    showSidebar() {
        this.visibleSidebar = true;
    }

    onGoBack() {
        switch (this.pathSplitFull[0]) {
            case "tool-management": {
                this.router.navigate(["/tool-management"]);
                break;
            }
            case "lifts": {
                let route = "/lifts";
                if (this.pathSplitFull.length > 2) {
                    route = route + "/" + this.pathSplitFull[1];
                }
                this.router.navigate([route]);
                break;
            }
            case "tool-maintenance-plans": {
                const previousToolMaintenancePlansUrl = this.navigationService.getPreviousUrl();
                if (previousToolMaintenancePlansUrl) {
                    return this.router.navigate([previousToolMaintenancePlansUrl]);
                } else {
                    this.navigationService.clearHistory();
                }

                let route = `/tool-maintenance-plans`;
                if (this.pathSplitFull.length > 3) {
                    route = route + "/" + this.pathSplitFull[1];
                }

                this.router.navigate([route]);
                break;
            }
            case "strain": {
                let route = "/strain";
                if (this.pathSplitFull.length > 2) {
                    route = route + "/" + this.pathSplitFull[1];
                }
                this.router.navigate([route]);
                break;
            }
            default:
                const previousUrl = this.navigationService.getPreviousUrl();
                if (previousUrl) {
                    return this.router.navigate([previousUrl]);
                } else {
                    this.navigationService.clearHistory();
                }

                this.router.navigate(["/" + this.pathSplitFull[0]]);
        }
    }

    logout() {
        this.logoutFired.emit();
    }

    onPlantsSelected(selectedPlants: any[]) {
        const plantIds = selectedPlants.map(plant => plant.id);
        this.selectedPlantsChange.emit(plantIds);
    }

    hasPermission(requiredPermission) {
        return this.currentUser.permissions && this.currentUser.permissions.indexOf(requiredPermission) > -1;
    }

    hasSection(section) {
        return this.permissionService.hasPermissionsForSection(this.currentUser, section);
    }

    onLinkClick(event) {
        this.visibleSidebar = false;
        this.removeOfflineNotifications(event);
    }

    removeOfflineNotifications(event) {
        const clickedElement = event.currentTarget;
        const offlineCounter = clickedElement.querySelector('.requests-counter--offline');

        if (clickedElement.hasChildNodes() && offlineCounter && offlineCounter.parentNode === clickedElement) {
            clickedElement.removeChild(offlineCounter);
        }
    }

    getSelectedPlants() {
        const selectedPlants = localStorage.getItem('selectedPlants');
        return selectedPlants ? selectedPlants.split(',') : [];
    }

    resetIncomingNotifications() {
        this.processLineToolRequests = 0;
        this.processLineToolReturns = 0;
        this.processFacilityToolRequests = 0;
        this.processFacilityToolRequests = 0;
    }

    getNotifications(plantIds: string[]) {
        this.authService.getMissedNotifications(plantIds).subscribe(
            (data) => {
                this._logger.info('getNotifications:: API successfully returned data: ', data);
                this.missedNotifications = data;
                this.resetIncomingNotifications();
            },
            (error) => {
                this._logger.info('getNotifications:: API returned error: ', error);
            }
        )
    }

    togglePlantSelection(plantId: number): void {
        if (this.isPlantSelected(plantId)) {
            this.selectedPlants = this.selectedPlants.filter((Id) => Id !== plantId);
        } else {
            this.selectedPlants.push(plantId);
        }
        const currentlySelected = _.filter(this.allPlants, (plant) => _.includes(this.selectedPlants, plant.id));
        this.onPlantsSelected(currentlySelected);
        this.buildKebabMenuItems();
    }

    onSidebarObjectsSelected(selectedObjects: any[]): void {
        const ids = selectedObjects.map(obj => obj.id);
        this.selectedSidebarItems = ids;
        this.sidebarItemsService.setSidebarItems(ids);

        this.selectedSidebarItemObjects = selectedObjects;
    }

    private syncSidebarObjects(): void {
        this.selectedSidebarItemObjects = this.availableSidebarItems.filter(item =>
        this.selectedSidebarItems.includes(item.id)
        );
    }

    private evaluateSectionSelector(): void {
        if (!this.currentUser) {
            this.showSectionSelector = false;
            return;
        }

        this.hasToolSection = this.permissionService.hasSectionClaim(this.currentUser, 'TOOLMANAGEMENT');
        this.hasStrainSection = this.permissionService.hasSectionClaim(this.currentUser, 'STRAIN');
        this.showSectionSelector = this.hasToolSection && this.hasStrainSection;

        if (!this.showSectionSelector) {
            const selection = this.hasToolSection ? ['tool'] : this.hasStrainSection ? ['strain'] : [];
            if (selection.length) {
                this.sidebarItemsService.setSidebarItems(selection);
            }
        }
    }

    private subscribeLiftOperatorToIncomingToolRequests() {
        this.newLiftOperatorToIncomingToolRequests = this.notificationService
            .liftOperatorRequestQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.processLineRequests) {
                            this.missedNotifications.toolRequests.processLineRequests = 0;
                        }

                        if (this.selectedPlantsIds.includes(String(value.plantId)) || !this.selectedPlantsIds.length) {
                            this.processLineToolRequests++;
                            this._logger.info('newLiftOperatorToIncomingToolRequests:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_LINE, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingToolRequests:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingToolReturns() {
        this.newLiftOperatorToIncomingToolReturns = this.notificationService
            .liftOperatorReturnQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.processLineRequests) {
                            this.missedNotifications.toolReturns.processLineRequests = 0;
                        }

                        if (this.selectedPlantsIds.includes(String(value.plantId)) && this.selectedPlantsIds.length) {
                            this.processLineToolReturns++;
                            this._logger.info('newLiftOperatorToIncomingToolReturns:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_LINES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingToolReturns:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingFacilityToolRequests() {
        this.newLiftOperatorToIncomingFacilityToolRequests = this.notificationService
            .liftOperatorFacilityRequestQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.processFacilityToolRequests++;
                            this._logger.info('newLiftOperatorToIncomingFacilityToolRequests:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_FACILITIES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingFacilityToolRequests:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingFacilityToolReturns() {
        this.newLiftOperatorToIncomingFacilityToolReturns = this.notificationService
            .liftOperatorFacilityReturnQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.processFacilityToolReturns++;
                            this._logger.info('newLiftOperatorToIncomingFacilityToolReturns:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_FACILITIES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingFacilityToolReturns:: Error while processing notification', error);
                });
    }

    buildKebabMenuItems(): any[] {
        return this.allPlants ? this.allPlants.map((plant) => ({
            label: plant.name,
            icon: this.isPlantSelected(plant.id) ? 'pi pi-check' : 'pi pi-circle', // Show check for selected plants
            command: () => this.togglePlantSelection(plant.id), // Toggle plant selection
        })) : [];
    }

    onHide() {

    }

    toggleKebabMenu($event: MouseEvent, menu: Menu) {
        if (menu) {
            menu.toggle($event);
        }
    }

    isPlantSelected(plantId: number): boolean {
        return this.selectedPlants?.some((Id) => Id === plantId);
    }

    onRoleSelected(roleOption: AssignedRoleOption) {
        if (roleOption && !this.isRoleSwitchInProgress) {
            this.roleChange.emit(roleOption);
        }
    }

    private translateSidebarItems(): void {
        const keys = this.sidebarItemDefinitions.map(item => item.translationKey);
        this.translateService.get(keys).subscribe(translations => {
            this.availableSidebarItems = this.sidebarItemDefinitions.map(item => ({
                id: item.id,
                name: translations[item.translationKey] || item.translationKey
            }));
            this.syncSidebarObjects();
        });
    }
}
